import { useCallback, useEffect, useState } from "react";
import { useSession } from "@clerk/clerk-react";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { toast } from "react-toastify";
import {
  BudgetBucketView,
  BucketTransactionView,
  CreateBudgetBucketDto,
  UpdateBudgetBucketDto,
} from "../types/budget-bucket";

/**
 * Custom event other components can dispatch to ask any mounted
 * `useBudgetBuckets` hook to refetch — e.g. after the items table picker
 * moves an investment line to a different bucket. The detail object should
 * include the affected building so unrelated tabs ignore the event.
 */
export const BUDGET_BUCKETS_CHANGED_EVENT = "budget-buckets-changed";

export interface BudgetBucketsChangedDetail {
  buildingId: string;
}

/**
 * Convenience helper: fire the cross-component refresh event from anywhere
 * (item picker handlers, status close flow, etc.). Wrapped so callers don't
 * have to construct the CustomEvent themselves.
 */
export const dispatchBudgetBucketsChanged = (buildingId: string) => {
  if (!buildingId) return;
  window.dispatchEvent(
    new CustomEvent<BudgetBucketsChangedDetail>(BUDGET_BUCKETS_CHANGED_EVENT, {
      detail: { buildingId },
    }),
  );
};

/**
 * Hook for managing strategic budget buckets on a building.
 *
 * The backend recomputes balances from the audit log on every read, so any
 * mutation through this hook just refetches the list — there is no client
 * state to keep in sync with computed totals.
 *
 * Also listens for the cross-component `BUDGET_BUCKETS_CHANGED_EVENT` so a
 * bucket change made elsewhere (e.g. in the investment items table picker)
 * triggers a refresh here without prop wiring.
 */
export const useBudgetBuckets = (buildingId: string | undefined) => {
  const { session } = useSession();
  const [buckets, setBuckets] = useState<BudgetBucketView[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const fetchBuckets = useCallback(async () => {
    if (!session || !buildingId) return;
    try {
      setIsLoading(true);
      const response = await fetchWithAuth(
        `${API_URL}/investment/budget-buckets?buildingId=${encodeURIComponent(
          buildingId,
        )}`,
        { method: "GET" },
        session,
      );
      if (!response.ok) {
        throw new Error(`Failed to fetch buckets: ${response.status}`);
      }
      const data: BudgetBucketView[] = await response.json();
      setBuckets(data || []);
    } catch (error) {
      console.error("[useBudgetBuckets] fetch error:", error);
      toast.error("Fout bij ophalen strategische budgetten");
    } finally {
      setIsLoading(false);
    }
  }, [session, buildingId]);

  useEffect(() => {
    fetchBuckets();
  }, [fetchBuckets]);

  // Cross-section sync: any component can dispatch the change event after a
  // mutation that affects bucket usage (e.g. moving a line in the items
  // table picker). Re-fetch when the event matches our building.
  useEffect(() => {
    if (!buildingId) return;
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<BudgetBucketsChangedDetail>).detail;
      if (detail?.buildingId && detail.buildingId !== buildingId) return;
      fetchBuckets();
    };
    window.addEventListener(BUDGET_BUCKETS_CHANGED_EVENT, handler);
    return () =>
      window.removeEventListener(BUDGET_BUCKETS_CHANGED_EVENT, handler);
  }, [buildingId, fetchBuckets]);

  const createBucket = useCallback(
    async (dto: CreateBudgetBucketDto): Promise<BudgetBucketView | null> => {
      if (!session) return null;
      try {
        const response = await fetchWithAuth(
          `${API_URL}/investment/budget-buckets`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(dto),
          },
          session,
        );
        if (!response.ok) {
          throw new Error(`Create bucket failed: ${response.status}`);
        }
        const created: BudgetBucketView = await response.json();
        toast.success(`Strategisch budget "${created.name}" aangemaakt`);
        await fetchBuckets();
        // Broadcast so any other mounted instance of this hook (e.g. the
        // items table's bucket picker options) also picks up the new pot
        // without a page reload.
        dispatchBudgetBucketsChanged(dto.buildingId);
        return created;
      } catch (error) {
        console.error("[useBudgetBuckets] create error:", error);
        toast.error("Fout bij aanmaken strategisch budget");
        return null;
      }
    },
    [session, fetchBuckets],
  );

  const updateBucket = useCallback(
    async (id: number, dto: UpdateBudgetBucketDto) => {
      if (!session) return;
      try {
        const response = await fetchWithAuth(
          `${API_URL}/investment/budget-buckets/${id}`,
          {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(dto),
          },
          session,
        );
        if (!response.ok) {
          throw new Error(`Update bucket failed: ${response.status}`);
        }
        await fetchBuckets();
        if (buildingId) dispatchBudgetBucketsChanged(buildingId);
      } catch (error) {
        console.error("[useBudgetBuckets] update error:", error);
        toast.error("Fout bij updaten strategisch budget");
      }
    },
    [session, fetchBuckets, buildingId],
  );

  const setApproval = useCallback(
    async (id: number, isApproved: boolean) => {
      if (!session) return;
      try {
        const response = await fetchWithAuth(
          `${API_URL}/investment/budget-buckets/${id}/approval`,
          {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ isApproved }),
          },
          session,
        );
        if (!response.ok) {
          throw new Error(`Approval toggle failed: ${response.status}`);
        }
        await fetchBuckets();
        if (buildingId) dispatchBudgetBucketsChanged(buildingId);
        toast.success(
          isApproved ? "strategisch budget goedgekeurd" : "Goedkeuring ingetrokken",
        );
      } catch (error) {
        console.error("[useBudgetBuckets] approval error:", error);
        toast.error("Fout bij wijzigen goedkeuring");
      }
    },
    [session, fetchBuckets, buildingId],
  );

  const deleteBucket = useCallback(
    async (id: number) => {
      if (!session) return;
      try {
        const response = await fetchWithAuth(
          `${API_URL}/investment/budget-buckets/${id}`,
          { method: "DELETE" },
          session,
        );
        if (!response.ok) {
          // Most likely cause: bucket still has investment lines attached.
          // Bubble the BE error message back to the user.
          let message = "Fout bij verwijderen strategisch budget";
          try {
            const data = await response.json();
            if (data?.message) message = data.message;
          } catch {
            /* ignore json parse errors */
          }
          throw new Error(message);
        }
        toast.success("Strategisch Budget verwijderd");
        await fetchBuckets();
        if (buildingId) dispatchBudgetBucketsChanged(buildingId);
      } catch (error: any) {
        console.error("[useBudgetBuckets] delete error:", error);
        toast.error(error?.message || "Fout bij verwijderen strategisch budget");
      }
    },
    [session, fetchBuckets, buildingId],
  );

  return {
    buckets,
    isLoading,
    refresh: fetchBuckets,
    createBucket,
    updateBucket,
    setApproval,
    deleteBucket,
  };
};

/**
 * Per-bucket audit log fetcher. Loaded lazily on demand (when the user opens
 * the drawer for a specific pot).
 */
export const useBucketTransactions = (bucketId: number | null) => {
  const { session } = useSession();
  const [transactions, setTransactions] = useState<BucketTransactionView[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const fetchTransactions = useCallback(async () => {
    if (!session || !bucketId) return;
    try {
      setIsLoading(true);
      const response = await fetchWithAuth(
        `${API_URL}/investment/budget-buckets/${bucketId}/transactions`,
        { method: "GET" },
        session,
      );
      if (!response.ok) {
        throw new Error(`Fetch transactions failed: ${response.status}`);
      }
      const data: BucketTransactionView[] = await response.json();
      setTransactions(data || []);
    } catch (error) {
      console.error("[useBucketTransactions] fetch error:", error);
      toast.error("Fout bij ophalen audit log");
    } finally {
      setIsLoading(false);
    }
  }, [session, bucketId]);

  useEffect(() => {
    if (bucketId) fetchTransactions();
    else setTransactions([]);
  }, [bucketId, fetchTransactions]);

  return { transactions, isLoading, refresh: fetchTransactions };
};
